import dayjs from "dayjs";
import { COMMON_FIELD_TYPES } from "../constants/fieldTypes";
import { errorMessages } from "../constants/frontendErrorMessages";
import validator from 'validator';
const GHANA_PHONE_NUMBER_PATTERN = /^\d{5,}$/;

// Validate MongoDB ObjectId format
export const isValidObjectId = (id) => /^[a-fA-F0-9]{24}$/.test(id);

// Validate email
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const trimmedEmail = email.trim();
  const isValidEmail =
    emailRegex.test(trimmedEmail) &&
    trimmedEmail.includes(".") &&
    trimmedEmail.split("@")[1]?.includes(".") &&
    trimmedEmail
      .split("@")[1]
      ?.split(".")
      .every((part) => part.length > 0);
  if (!isValidEmail) return errorMessages.INVALID_EMAIL;
  return "";
};

export const validateEmailWithExistence = async (
  email,
  checkEmailExistsFunc
) => {
  // Handle empty email case
  if (!email || email.trim() === "") {
    return ""; 
  }

  // First validate email format
  const isValidEmail = validator.isEmail(email.trim());
  if (!isValidEmail) {
    return errorMessages.INVALID_EMAIL;
  }

  // Then check if email exists 
  try {
    const result = await checkEmailExistsFunc(email.trim());
    console.log("Email existence check result:", result);
    if (result.response.data.exists) {
      return errorMessages.EMAIL_ALREADY_EXISTS;
    }
    return "";
  } catch (error) {
    console.error("Error checking email existence:", error);
    return "";
  }
};

// FUNCTION: Validate Phone Number
function validatePhone(phoneNumber) {
  // INFO: Ghana Mobile & Landline Numbers
  // Mobile: 10 digits, starting with 02X, 05X, or 024-059
  // Landline: 9 digits, starting with 03X

  if (!phoneNumber) {
    return errorMessages.REQUIRED_FIELD;
  } else if (!GHANA_PHONE_NUMBER_PATTERN.test(phoneNumber)) {
    return errorMessages.INVALID_PHONE_NUMBER;
  }

  return "";
}
// EXAMPLE:
// console.log(validatePhone("+233245678901")); // true
// console.log(validatePhone("0245678901")); // true
// console.log(validatePhone("1234567890")); // false

// Validate email or phone number
const validateEmailOrPhone = (input) => {
  if (!input) {
    return errorMessages.REQUIRED_FIELD;
  } else if (validateEmail(input) === "") {
    // valid email
    return "";
  } else if (validatePhone(input) === "") {
    // valid phone number
    return "";
  }

  return errorMessages.INVALID_EMAIL_OR_PHONE; // invalid email or phone number
};

// Validate password
const validatePassword = (password) => {
  const hasMinLength = password.length >= 6;

  if (!hasMinLength) {
    return errorMessages.INVALID_PASSWORD;
  }

  return "";
};

// FUNCTION: Validate number
const isValidNumber = (input) => {
  // Check if the input is of type "number" or can be converted to a valid number
  if (typeof input === "number" && !isNaN(input)) {
    return "";
  }
  if (
    typeof input === "string" &&
    !isNaN(parseFloat(input)) &&
    isFinite(input)
  ) {
    return "";
  }
  return errorMessages.INVALID_NUMBER;
};

// FUNCTION: Validate date
const isValidDate = (dateString) => {
  // console.log("dateString", dateString);

  // If no date provided
  if (!dateString) return false;
  dateString = dayjs(dateString);

  // If it's a dayjs object, format it first
  if (dayjs.isDayjs(dateString)) {
    dateString = dateString.format("YYYY-MM-DD");
    // console.log("dayjs formatted date", dateString);
  }

  // Check format (YYYY-MM-DD)
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateFormatRegex.test(dateString)) return false;

  // Parse the date parts
  const [year, month, day] = dateString.split("-").map(Number);

  // Create a new Date object and verify the values
  const date = new Date(year, month - 1, day); // month is 0-based in JavaScript
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set time to start of the day

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    year >= 1900 && // Reasonable minimum year
    year <= 2100 && // Reasonable maximum year
    date >= today // Ensure the date is not in the past
  );
};
// USAGE:
// EXAMPLE:
/*
  console.log(isValidDate('2025-01-25')); // true
  console.log(isValidDate('2025-13-25')); // false (invalid month)
  console.log(isValidDate('2025-02-30')); // false (invalid day)
  console.log(isValidDate('202/02/20'));  // false (wrong format)
  console.log(isValidDate(''));           // false
  console.log(isValidDate(null));         // false
  console.log(isValidDate(dayjs()));      // true (current date)
*/

// FUNCTION: Validate time
const isValidTime = (time) => {
  // Regex for 12-hour format (e.g., 01:30 AM, 12:45 PM)
  const twelveHourRegex = /^(0[1-9]|1[0-2]):([0-5][0-9])\s?(AM|PM)$/i;

  // Regex for 24-hour format (e.g., 14:50, 00:15)
  const twentyFourHourRegex = /^([01]\d|2[0-3]):([0-5][0-9])$/;

  return twelveHourRegex.test(time) || twentyFourHourRegex.test(time);
};

// FUNCTION: Ghana Nic Number Validation - Format Should be like this GHA-XXXXXXXXX-X
function isValidGhanaIdNumber(pin) {
  // INFO:
  //  A three-letter country code (e.g., 'GHA' for Ghana),
  //  Followed by a hyphen,
  //  Nine digits,
  //  Another hyphen,
  //  And a single check digit.

  // // USAGE: Regular expression to match the Ghana Card PIN format
  // const pinRegex = /^GHA-\d{9}-\d$/;
  // return pinRegex.test(pin);

  // Check if the input is a string and has a minimum length of 1
  if (typeof pin !== "string" || pin.length < 1) {
    return false;
  }
}

//Validate ONE_OF_TWO_REQUIRED (either field1 OR field2 must be filled)
const validateOneOfTwoRequired = (value, otherFieldValue, fieldConfig, requiredFields, name) => {
  const isCurrentFieldFilled = value && value.toString().trim() !== "";
  const isOtherFieldFilled = otherFieldValue && otherFieldValue.toString().trim() !== "";
  
  if (!isCurrentFieldFilled && !isOtherFieldFilled && requiredFields[name]) {
    return "ONE_OF_TWO_REQUIRED"; // Return a key
  }
  
  return "";
};

const validateRadioGroup = (value, requiredFields, name) => {
  if (requiredFields[name] && (!value || value.trim() === "")) {
    return "RADIO_GROUP_REQUIRED"; // Return a key
  }
  return "";
};

//  Validate CHECKBOX_GROUP (at least one must be checked)
const validateCheckboxGroup = (value, requiredFields, name) => {
  if (!requiredFields[name]) return "";
  
  // value should be an object like { option1: true, option2: false, ... }
  if (typeof value !== "object" || value === null) {
    return "Invalid checkbox group value.";
  }
  
  // Check if at least one checkbox is true
  const hasAtLeastOneChecked = Object.values(value).some(val => val === true);
  
  if (!hasAtLeastOneChecked) {
    return "Please select at least one option.";
  }
  
  return "";
};

// Main validation field function
export const validateField = (name, type, value, formData, requiredFields) => {
  if (!type) return "Error";

  let error = "";
  const fieldType = typeof type === "object" ? type.type : type;
  const fieldConfig = typeof type === "object" ? type : {};

  switch (fieldType) {
    case COMMON_FIELD_TYPES.REQUIRED_FIELD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      break;

    case COMMON_FIELD_TYPES.BOOLEAN_FIELD:
      if (!value && value !== false && requiredFields[name])
        error = errorMessages.REQUIRED_FIELD;
      else if (
        !(!value && value !== false) &&
        !(value === false || value === true)
      )
        error = errorMessages.INVALID_OPTION;
      break;

    case COMMON_FIELD_TYPES.NUMBER_FIELD:
      if (!value && value !== false && requiredFields[name])
        error = errorMessages.REQUIRED_FIELD;
      else if (value) error = isValidNumber(value);
      break;

    case COMMON_FIELD_TYPES.FIRST_NAME:
      if (!value && value !== false && requiredFields[name]) {
        error = errorMessages.FIRST_NAME_REQUIRED;
      }
      break;

    case COMMON_FIELD_TYPES.LAST_NAME:
      if (!value && value !== false && requiredFields[name]) {
        error = errorMessages.LAST_NAME_REQUIRED;
      }
      break;

    case COMMON_FIELD_TYPES.EMAIL_FIELD:
    case COMMON_FIELD_TYPES.EMAIL:
      if (!value && requiredFields[name]) {
        error = errorMessages.EMAIL_REQUIRED;
      } else if (value) {
        const trimmedValue = value.trim();
        if (!validator.isEmail(trimmedValue)) {
          error = errorMessages.INVALID_EMAIL; 
        }
      }
      break;

    case COMMON_FIELD_TYPES.PHONE:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value) error = validatePhone(value);
      break;

    case COMMON_FIELD_TYPES.PASSWORD_FIELD:
      if (!value && requiredFields[name])
        error = errorMessages.PASSWORD_REQUIRED;
      else if (value) error = validatePassword(value);
      break;

    case COMMON_FIELD_TYPES.CONFIRM_PASSWORD:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (value !== formData.password)
        error = errorMessages.PASSWORD_MISMATCH;
      break;

    case COMMON_FIELD_TYPES.USERNAME:
      if (!value && requiredFields[name])
        error = errorMessages.USERNAME_REQUIRED;
      else if (value && value.length < 4)
        error = errorMessages.USERNAME_REQUIRED;
      else if (value && !/^[a-zA-Z0-9_-]+$/.test(value))
        error = errorMessages.INVALID_USERNAME;
      break;

    case COMMON_FIELD_TYPES.DJ_NAME:
      if (!value && requiredFields[name]) error = "";

      break;

    case COMMON_FIELD_TYPES.COUNTRY:
      if (!value && requiredFields[name])
        error = errorMessages.COUNTRY_REQUIRED;
      break;

    case COMMON_FIELD_TYPES.STATE:
      if (!value && requiredFields[name]) error = errorMessages.STATE_REQUIRED;
      break;

    case COMMON_FIELD_TYPES.DATE_PICKER:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value && !isValidDate(value)) {
        // console.log("trying to validate date", value);
        error = errorMessages.INVALID_DATE;
      }
      break;

    case COMMON_FIELD_TYPES.TIME_PICKER:
      if (!value && requiredFields[name]) error = errorMessages.REQUIRED_FIELD;
      else if (!isValidTime(value)) error = errorMessages.INVALID_TIME;
      break;

    case COMMON_FIELD_TYPES.ID_NUMBER:
      if (!value && requiredFields[name]) {
        // console.log("ID Number is required");
        error = errorMessages.REQUIRED_FIELD;
      } else if (isValidGhanaIdNumber(value)) {
        error = errorMessages.INVALID_ID_NUMBER;
      }
      break;

    case COMMON_FIELD_TYPES.EMAIL_OR_PHONE:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value) {
        error = validateEmailOrPhone(value);
      }
      break;

    case COMMON_FIELD_TYPES.COUNTRY_REQUIRED:
      if ((!value || Object.keys(value).length === 0) && requiredFields[name]) {
        error = errorMessages.COUNTRY_REQUIRED;
      } else if (typeof value !== "object" || !value.hasOwnProperty("id"))
        error = errorMessages.INVALID_OPTION;
      break;

    case COMMON_FIELD_TYPES.URL:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      } else if (value) {
        // Trim the value (same as backend .trim())
        const trimmedValue = value.trim();
        console.log("trimmedValue", trimmedValue);
        if (!validator.isURL(trimmedValue)) {
          error = errorMessages.INVALID_URL;
        }
      }
      break;
    case COMMON_FIELD_TYPES.TEXT_FIELD:
      if (!value && requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
      }
      break;

    case COMMON_FIELD_TYPES.MULTIPLE_DROPDOWN:
      if (!value || (Array.isArray(value) && value.length === 0)) {
        if (requiredFields[name]) {
          error = errorMessages.REQUIRED_FIELD;
        }
      } else if (Array.isArray(value)) {
        // Check if all selected options are valid
        for (const option of value) {
          if (!option || !option.id) {
            error = errorMessages.INVALID_OPTION;
            break;
          }
        }
      } else {
        error = errorMessages.INVALID_OPTION;
      }
      break;

    case COMMON_FIELD_TYPES.CHECKBOX:
      if (requiredFields[name]) {
        error = errorMessages.REQUIRED_FIELD;
        break;
      }
      if (value === undefined || value === null) {
        error = errorMessages.INVALID_OPTION;
      }
      break;

  
    case COMMON_FIELD_TYPES.ONE_OF_TWO_REQUIRED:
      const otherFieldName = fieldConfig.otherField;
      const otherFieldValue = formData[otherFieldName];
      error = validateOneOfTwoRequired(value, otherFieldValue, fieldConfig, requiredFields, name);
      break;

    
    case COMMON_FIELD_TYPES.RADIO_GROUP:
      error = validateRadioGroup(value, requiredFields, name);
      break;

    
    case COMMON_FIELD_TYPES.CHECKBOX_GROUP:
      error = validateCheckboxGroup(value, requiredFields, name);
      break;

    default:
      break;
  }

  return error;
};
